import { SliderThumb, SliderTrack } from '@mui/material'
import { forwardRef } from 'react'
import { SLIDER_CHROME_RADIUS, SLIDER_THUMB_SIZE } from './colorSelectorSlider.styles'

const SLIDER_THUMB_INSET = SLIDER_THUMB_SIZE / 2
const SLIDER_TRAVEL_EXPR = `100% - ${SLIDER_THUMB_SIZE}px`

const parsePercent = (value: string | number | undefined): number | null => {
    if (value == null) return null
    if (typeof value === 'number') return value
    const match = /^([\d.]+)%$/.exec(String(value).trim())
    return match ? parseFloat(match[1]) : null
}

const remapThumbStyle = (style?: React.CSSProperties): React.CSSProperties | undefined => {
    if (!style) return style
    const percent = parsePercent(style.left)
    if (percent == null) return style
    return {
        ...style,
        left: `calc(${SLIDER_THUMB_INSET}px + (${SLIDER_TRAVEL_EXPR}) * ${percent / 100})`,
    }
}

/** Keeps thumb inside the bordered chrome; at min value the thumb's center sits inset px from the left inner edge. */
export const ColorSelectorSliderThumb = forwardRef<HTMLSpanElement, React.ComponentProps<typeof SliderThumb>>(
    function ColorSelectorSliderThumb({ style, ...props }, ref) {
        return <SliderThumb {...props} ref={ref} style={remapThumbStyle(style)} />
    }
)

const remapTrackStyle = (style?: React.CSSProperties): React.CSSProperties | undefined => {
    if (!style) return style
    const widthPercent = parsePercent(style.width)
    if (widthPercent == null) return style
    return {
        ...style,
        left: 0,
        top: 0,
        bottom: 0,
        height: '100%',
        width: `calc(${SLIDER_THUMB_INSET}px + (${SLIDER_TRAVEL_EXPR}) * ${widthPercent / 100})`,
        transform: 'none',
        borderRadius: 0,
        borderTopLeftRadius: SLIDER_CHROME_RADIUS,
        borderBottomLeftRadius: SLIDER_CHROME_RADIUS,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    }
}

/** Fill grows from the left inner border (0) toward the thumb. */
export const ColorSelectorSliderTrack = forwardRef<HTMLSpanElement, React.ComponentProps<typeof SliderTrack>>(
    function ColorSelectorSliderTrack({ style, ...props }, ref) {
        return <SliderTrack {...props} ref={ref} style={remapTrackStyle(style)} />
    }
)

export const colorSelectorSliderSlots = {
    thumb: ColorSelectorSliderThumb,
    track: ColorSelectorSliderTrack,
} as const
